import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ListItem,
  Progress,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { isNotEmptyObject } from "@chakra-ui/utils";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { zxcvbn } from "@zxcvbn-ts/core";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import {
  colorSchemeForPasswordScore,
  MIN_PASSWORD_SCORE,
  passwordSetupSchema,
} from "features/auth/AuthSchemas";
import { useStytchFormErrorHandler } from "features/auth/StytchUtils";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import Form from "shared/components/form/Form";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { useToast } from "shared/components/toast/useToast";
import { isTruthy } from "shared/utils/utils";

export interface AuthPasswordCreateFormData {
  password: string;
}

interface AuthPasswordCreateFormProps {
  email: string | undefined;
  onSubmit: (data: AuthPasswordCreateFormData) => void;
  onSuccess: () => void;
  onCancel: () => void;
  isLoading: boolean;
  successMessage?: string;
}

export const AuthPasswordCreateForm: React.FC<AuthPasswordCreateFormProps> = ({
  email,
  onSubmit,
  onSuccess,
  onCancel,
  isLoading,
  successMessage,
}) => {
  const { t } = useTranslation(["auth", "common"]);
  const { showErrorToast } = useToast();
  const onStytchError = useStytchFormErrorHandler();

  const form = useForm<AuthPasswordCreateFormData>({
    resolver: yupResolver(passwordSetupSchema),
    context: { email },
    defaultValues: {
      password: "",
    },
  });

  const password = useWatch({
    control: form.control,
    name: "password",
  });

  const result = zxcvbn(password, [email].filter(isTruthy));
  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      onError={async (error, values, context) => {
        const handled = await onStytchError(error, values, context);
        if (!handled) {
          showErrorToast(t("common:error.passwordInvalid"));
        }
        return true;
      }}
      successMessage={successMessage}
    >
      <Card width="md">
        <CardHeader>
          <Box>
            <Text textStyle="h4">{t("auth:setPassword")}</Text>
            {email && (
              <Text pt="24px">
                <Trans
                  ns="auth"
                  i18nKey="setup.passwordInstructions"
                  values={{ email }}
                >
                  Set a password for
                  <Text as="span" textStyle="bodyBold">
                    user@example.com
                  </Text>
                </Trans>
              </Text>
            )}
          </Box>
          <CardCloseButton onClick={onCancel} />
        </CardHeader>
        <CardBody pb={0}>
          <FormTextInput
            name="password"
            type="password"
            autoComplete="new-password"
            title={t("auth:password")}
            autoFocus={true}
            showMessages={false}
            controlProps={{
              mb: 0,
            }}
            shouldUnregister={false}
          />
          <Progress
            size="xs"
            my="8px"
            max={4}
            value={Math.max(password ? 0.2 : 0, result.score)}
            colorScheme={colorSchemeForPasswordScore[result.score]}
          />
          {isNotEmptyObject(form.formState.errors) &&
            result.score < MIN_PASSWORD_SCORE && (
              <Alert status="error" alignItems="flex-tart" size="sm">
                <AlertDescription>
                  <Text textStyle="smallBold">
                    {result.feedback.warning ||
                      t("common:error.passwordTooShort")}
                  </Text>
                  {result.feedback.suggestions.length && (
                    <UnorderedList>
                      {result.feedback.suggestions.map((suggestion, index) => (
                        <ListItem key={index} textStyle="small">
                          {suggestion}
                        </ListItem>
                      ))}
                    </UnorderedList>
                  )}
                </AlertDescription>
              </Alert>
            )}
        </CardBody>
        <CardFooter>
          <AuthHelpButton />
          <Button type="submit" isLoading={isLoading}>
            {t("common:continue")}
          </Button>
        </CardFooter>
      </Card>
    </Form>
  );
};
